import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Providers } from './providers';
import { WebLayout, PanelLayout } from '@kss/layouts';
import { RequireAuth, useSessionBootstrap, SessionTimeoutGuard } from '@kss/auth';

import Home from './pages/Home';
import Landing from './pages/Landing';
import About from './pages/About';
import Contact from './pages/Contact';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import TenantSignup from './pages/TenantSignup';
// E-commerce template (shop / account / demo / sample) removed 2026-05-07
// after live API audit confirmed no Cart/Checkout/PublicCatalog/Banner
// controllers exist on api.ebillbook.kristysoftware.com.

import { renderPanelRoutes } from './panelRoutes';
import LoginModal from './components/LoginModal';
import OfflineBadge from '../../panel/src/offline/OfflineBadge';
import BranchSwitcher from '../../panel/src/branch/BranchSwitcher';
import { initOfflineQueue } from '../../panel/src/offline/posQueue';
import { setupBranchHeader } from '../../panel/src/branch/setupBranchHeader';

import PortalLayout from './portal/PortalLayout';
import PortalLogin from './portal/PortalLogin';
import PortalDashboard from './portal/PortalDashboard';
import PortalInvoices from './portal/PortalInvoices';
import PortalPayments from './portal/PortalPayments';

import DineLayout from './dine/DineLayout';
import DineHome from './dine/DineHome';
import DineMenu from './dine/DineMenu';
import DineOrders from './dine/DineOrders';
import DinePayBill from './dine/DinePayBill';

// Wire the offline queue + branch header once for the embedded panel mount.
initOfflineQueue();
setupBranchHeader();

function SessionBootstrap() {
  useSessionBootstrap();
  return null;
}

export default function App() {
  return (
    <Providers>
      <BrowserRouter>
        <SessionBootstrap />
        <SessionTimeoutGuard idleMinutes={15} warnSeconds={60} />
        <LoginModal />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/landing" element={<Landing />} />
          <Route path="/signup" element={<TenantSignup />} />

          <Route element={<WebLayout />}>
            <Route path="/home" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>

          <Route
            path="/panel"
            element={
              <RequireAuth redirectTo="/login">
                <PanelLayout
                  topbarExtras={
                    <>
                      <BranchSwitcher />
                      <OfflineBadge />
                    </>
                  }
                />
              </RequireAuth>
            }
          >
            {renderPanelRoutes()}
          </Route>

          <Route path="/portal/login" element={<PortalLogin />} />
          <Route path="/portal" element={<PortalLayout />}>
            <Route index element={<PortalDashboard />} />
            <Route path="invoices" element={<PortalInvoices />} />
            <Route path="payments" element={<PortalPayments />} />
          </Route>

          {/* Guest dine-in QR flow.
              URL: /dine/<tenantCode>/t/<qrToken>
              No auth gate at the route level — the table QR token IS the
              session context. Customer identity is collected mid-flow
              via OTP when they tap "Place Order". */}
          <Route path="/dine/:tenantCode/t/:qrToken" element={<DineLayout />}>
            <Route index element={<DineHome />} />
            <Route path="menu" element={<DineMenu />} />
            <Route path="orders" element={<DineOrders />} />
            <Route path="pay-bill" element={<DinePayBill />} />
          </Route>

          {/* Shorter QR URL — tenant is taken from the SUBDOMAIN
              (bill.kristysoftware.com → "bill"), so the printed QR only
              needs the table token: /dine/t/<qrToken>. */}
          <Route path="/dine/t/:qrToken" element={<DineLayout />}>
            <Route index element={<DineHome />} />
            <Route path="menu" element={<DineMenu />} />
            <Route path="orders" element={<DineOrders />} />
            <Route path="pay-bill" element={<DinePayBill />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </Providers>
  );
}
